import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { TextField, CircularProgress, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { toast } from 'react-toastify';
import Draggable from "react-draggable";
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
// import ReactDOM from "react-dom"; // Unused - kept for reference if needed elsewhere

import CreateRelation from './prop/CreateRelation';
import { sendCommunication, getCommunicationResources } from '../../store/Activities';
import { getIntegrationStatus } from '../../store/Integration';

// Reusable Alert Modal Component
const AlertModal = ({ isOpen, toggle, message }) => (
  <Modal isOpen={isOpen} toggle={toggle} className="main-modal new-user-modal modal-dialog-centered">
    <ModalHeader toggle={toggle}></ModalHeader>
    <ModalBody>
      <div className="add-content">
        <h2>{message}</h2>
        <div className="templates-inner">
          <div className="btn-group">
            <button className="btn-style-new" onClick={toggle}>Ok</button>
          </div>
        </div>
      </div>
    </ModalBody>
  </Modal>
);

const MakeCall = ({ clearActivityForm, onCreateActivity, isActiveExpande, setIsActiveExpande, isInSoftphone = false, onClose, isInDialpad = false }) => {
  const dispatch = useDispatch();

  const { associations, communication } = useSelector(state => state['Activities']);
  const { singlePageData } = useSelector(state => state['SinglePage']);
  const callConnection = communication?.['call'] || {};
  const isSending = callConnection['sending'] ?? false;

  // Get loading state from Activities (for resources loading)
  const resourcesLoading = callConnection['loading'] ?? false;

  // Get connection status from Integration store (unified approach)
  const integrationStatus = useSelector(state => state['Integration']['integrationStatus']);
  // Always use lowercase key for consistency (saga normalizes all keys to lowercase)
  const twilioStatus = integrationStatus?.twilio;
  const isIntegrationLoading = twilioStatus?.loading ?? true;
  const hasIntegrationConnection = twilioStatus?.data?.isConnected ?? false;

  const [toNumber, setToNumber] = useState('');
  const [agentId, setAgentId] = useState('');
  const [phoneId, setPhoneId] = useState('');
  const [modalDroppedAlert, setModalDroppedAlert] = useState(false);

  // Combined loading state: integration status loading OR resources loading
  const isLoadingResources = isIntegrationLoading || resourcesLoading;
  const agents = callConnection['agents'] || [];
  const phones = callConnection['phones'] || [];
  const hasAgents = agents.length > 0;
  const hasPhones = phones.length > 0;
  const hasCallCapability = hasAgents && hasPhones;
  const [modalDroppedAlertTwo, setModalDroppedAlertTwo] = useState(false);
  const [modalDroppedAlertFaild, setModalDroppedAlertFaild] = useState(false);
  const [modalDroppedAlertPhone, setModalDroppedAlertPhone] = useState(false);
  const [callError, setCallError] = useState('');
  
  // Draggable state - only used when not in dialpad
  const [activeDrags, setActiveDrags] = useState(0);
  // const [deltaPosition, setDeltaPosition] = useState({ x: 0, y: 0 }); // Unused - kept for reference
  // const [controlledPosition, setControlledPosition] = useState({ x: -400, y: 200 }); // Unused - kept for reference
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    // Fetch integration status and call resources
    dispatch(getIntegrationStatus('Twilio'));
    dispatch(getCommunicationResources('call'));
    if (singlePageData && singlePageData['phone']) setToNumber(singlePageData['phone']);
  }, [singlePageData, dispatch]);

  const handleInitiateCall = () => {
    if (!toNumber.trim()) return setModalDroppedAlert(true);
    if (!agentId || !phoneId) return setModalDroppedAlertPhone(true);

    dispatch(sendCommunication({
      channel: 'call',
      contactId: singlePageData?.['_id'],
      toNumber: toNumber,
      agentId: agentId,
      phoneId: phoneId,
      successCallback: (data) => {
        setToNumber('');
        clearActivityForm();
        if (data?.['roomUrl']) window.open(data['roomUrl'], '_blank');
      }
    }));
  };

  // Draggable handlers - only used when not in dialpad
  const onStart = () => setActiveDrags(d => d + 1);
  const onStop = () => setActiveDrags(d => d - 1);
  const dragHandlers = { onStart, onStop };

  // Unused drag handlers
  // const handleDrag = useCallback((e, ui) => {
  //   setDeltaPosition(prev => ({
  //     x: prev.x + ui.deltaX,
  //     y: prev.y + ui.deltaY,
  //   }));
  // }, []);
  // const onDrop = (e) => {
  //   setActiveDrags(d => d - 1);
  //   if (e.target.classList.contains("drop-target")) {
  //     setModalDroppedAlertTwo(true);
  //     e.target.classList.remove("hovered");
  //   }
  // };
  // const onDropAreaMouseEnter = (e) => {
  //   if (activeDrags) e.target.classList.add("hovered");
  // };
  // const onDropAreaMouseLeave = (e) => {
  //   e.target.classList.remove("hovered");
  // };
  // const adjustXPos = (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   setControlledPosition(prev => ({ x: prev.x - 10, y: prev.y }));
  // };
  // const adjustYPos = (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   setControlledPosition(prev => ({ x: prev.x, y: prev.y - 10 }));
  // };
  // const onControlledDrag = (e, position) => {
  //   const { x, y } = position;
  //   setControlledPosition({ x, y });
  // };
  // const onControlledDragStop = (e, position) => {
  //   onControlledDrag(e, position);
  //   onStop();
  // };

  // Modal handlers - only used when in softphone modal
  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    } else if (clearActivityForm) {
      clearActivityForm();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && isInSoftphone && !isInDialpad) {
      handleClose();
    }
  };

  if (!isOpen && isInSoftphone && !isInDialpad) return null;

  if (isLoadingResources) {
    const content = (
      <div className={`add-note-sec call-single-box ${isInSoftphone ? 'softphone-call-box' : isInDialpad ? 'dialpad-full-page' : isActiveExpande ? 'expande-box' : ''}`}>
        <div className="add-note-box">
          <div className="add-note-header">
            <div className="left-area">
              <h4>Make Call</h4>
            </div>
            {!isInSoftphone && !isInDialpad && (
              <div className="right-area">
                <button className="close-btns" onClick={() => setIsActiveExpande(prev => !prev)}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.45508 7.9541L2.69886 11.8543L6.59902 11.6105" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7.64551 2.07227L11.5457 1.82855L11.7895 5.72871" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M11.5444 1.82812L2.69819 11.8538" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button className="close-btns" onClick={() => { clearActivityForm(); setIsActiveExpande(false); }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="bottom-note-box">
            <div className="loading-resources-container">
              <CircularProgress size={24} />
              <span className="loading-resources-text">Loading call resources...</span>
            </div>
          </div>
        </div>
      </div>
    );

    if (isInSoftphone && !isInDialpad) {
      return (
        <div className="softphone-modal-overlay" onClick={handleBackdropClick}>
          {content}
        </div>
      );
    }
    return content;
  }

  if (!hasCallCapability) {
    const missingAgents = !hasAgents;
    const missingPhones = !hasPhones;

    const content = (
      <div className={`add-note-sec call-single-box ${isInSoftphone ? 'softphone-call-box' : isInDialpad ? 'dialpad-full-page' : isActiveExpande ? 'expande-box' : ''}`}>
        <div className="add-note-box">
          <div className="add-note-header">
            <div className="left-area">
              <h4>Make Call</h4>
            </div>
            {!isInSoftphone && !isInDialpad && (
              <div className="right-area">
                <button className="close-btns" onClick={() => setIsActiveExpande(prev => !prev)}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.45508 7.9541L2.69886 11.8543L6.59902 11.6105" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7.64551 2.07227L11.5457 1.82855L11.7895 5.72871" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M11.5444 1.82812L2.69819 11.8538" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button className="close-btns" onClick={() => { clearActivityForm(); setIsActiveExpande(false); }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="bottom-note-box">
            <div className="celander-box call-box">
              <h4>
                <i className='icon'>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.01 15.38C18.78 15.38 17.59 15.18 16.48 14.82C16.13 14.7 15.74 14.79 15.47 15.06L13.9 17.03C11.07 15.68 8.42 13.13 7.01 10.2L8.96 8.54C9.23 8.26 9.31 7.87 9.2 7.52C8.83 6.41 8.64 5.22 8.64 3.99C8.64 3.45 8.19 3 7.65 3H4.19C3.65 3 3 3.24 3 3.99C3 13.28 10.73 21 20.01 21C20.72 21 21 20.37 21 19.82V16.37C21 15.83 20.55 15.38 20.01 15.38Z" fill="#4B5563"/>
                  </svg>
                </i>
                {missingAgents && missingPhones && 'Setup Required: Agents & Phone Numbers'}
                {missingAgents && !missingPhones && 'Agents Not Configured'}
                {!missingAgents && missingPhones && 'Phone Numbers Not Configured'}
              </h4>
              <p>
                {missingAgents && missingPhones &&
                  'You need both agents and phone numbers to make calls. Set them up to start connecting with customers.'}
                {missingAgents && !missingPhones &&
                  'Create an agent to handle your outbound calls and conversations.'}
                {!missingAgents && missingPhones &&
                  'Connect a phone number to start making calls with your agents.'}
              </p>
              <div className="btn-area setup-buttons-container">
                {missingAgents && (
                  <button
                    className='btn-style-new'
                    onClick={() => window.location.href = '/agents'}
                  >
                    {missingPhones ? 'Setup Agents' : 'Go to Agents'}
                  </button>
                )}
                {missingPhones && (
                  <button
                    className={missingAgents ? 'btn-style-border' : 'btn-style-new'}
                    onClick={() => window.location.href = '/phones'}
                  >
                    {missingAgents ? 'Setup Phone Numbers' : 'Go to Phone Numbers'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    if (isInSoftphone && !isInDialpad) {
      return (
        <div className="softphone-modal-overlay" onClick={handleBackdropClick}>
          {content}
        </div>
      );
    }
    return content;
  }

  const formContent = (
    <div className={`add-note-sec call-single-box ${isInSoftphone ? 'softphone-call-box' : isInDialpad ? 'dialpad-full-page' : isActiveExpande ? 'expande-box' : ''}`}>
      <div className="add-note-box">
        <div className="add-note-header">
          <div className="left-area">
            <h4>Make Call</h4>
          </div>
          {!isInSoftphone && !isInDialpad && (
            <div className="right-area">
              <button className="close-btns" onClick={() => setIsActiveExpande(prev => !prev)}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.45508 7.9541L2.69886 11.8543L6.59902 11.6105" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M7.64551 2.07227L11.5457 1.82855L11.7895 5.72871" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M11.5444 1.82812L2.69819 11.8538" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button className="close-btns" onClick={() => { clearActivityForm(); setIsActiveExpande(false); }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
                </svg>
              </button>
            </div>
          )}
        </div>
            <div className="bottom-note-box">
              <div className="group-form">
                <label>Select Agent</label>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel></InputLabel>
                  <Select
                    value={agentId}
                    onChange={(e) => setAgentId(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Select an agent</em>
                    </MenuItem>
                    {agents.map((agent) => (
                      <MenuItem key={agent.id} value={agent.id}>
                        {agent.name} ({agent.type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className="group-form">
                <label>Select Phone</label>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel></InputLabel>
                  <Select
                    value={phoneId}
                    onChange={(e) => setPhoneId(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Select a phone number</em>
                    </MenuItem>
                    {phones.map((phone) => (
                      <MenuItem key={phone.id} value={phone.id}>
                        {phone.number} - {phone.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className="group-form">
                <label>To</label>
                <TextField
                  type="tel"
                  fullWidth
                  size="small"
                  value={toNumber}
                  onChange={(e) => setToNumber(e.target.value)}
                  placeholder="Enter phone number"
                  variant="outlined"
                />
              </div>


              {/* Related objects */}
              {/* <CreateRelation
                type="new"
                initialAssociations={associations}
              /> */}
            </div>
            <div className="add-note-footer make-call-footer">
              <button
                className='btn-style-new'
                onClick={handleInitiateCall}
                disabled={isSending || !toNumber.trim() || !agentId || !phoneId}
              >
                {isSending ? 'Initiating...' : 'Start Call'}
              </button>
            </div>
          </div>
        </div>
  );

  // Render for dialpad - full page without draggable or modal overlay
  if (isInDialpad) {
    return (
      <>
        {formContent}
        {/* Alert Modals */}
        <AlertModal isOpen={modalDroppedAlert} toggle={() => setModalDroppedAlert(false)} message="Please enter a phone number" />
        <AlertModal isOpen={modalDroppedAlertPhone} toggle={() => setModalDroppedAlertPhone(false)} message="Please select an agent and phone number" />
        <AlertModal isOpen={modalDroppedAlertFaild} toggle={() => setModalDroppedAlertFaild(false)} message={callError || 'Failed to initiate call'} />
        <AlertModal isOpen={modalDroppedAlertTwo} toggle={() => setModalDroppedAlertTwo(false)} message="Dropped" />
      </>
    );
  }

  if (isInSoftphone) {
    return (
      <>
        <div className="softphone-modal-overlay" onClick={handleBackdropClick}>
          {formContent}
        </div>

        {/* Alert Modals */}
        <AlertModal isOpen={modalDroppedAlert} toggle={() => setModalDroppedAlert(false)} message="Please enter a phone number" />
        <AlertModal isOpen={modalDroppedAlertPhone} toggle={() => setModalDroppedAlertPhone(false)} message="Please select an agent and phone number" />
        <AlertModal isOpen={modalDroppedAlertFaild} toggle={() => setModalDroppedAlertFaild(false)} message={callError || 'Failed to initiate call'} />
        <AlertModal isOpen={modalDroppedAlertTwo} toggle={() => setModalDroppedAlertTwo(false)} message="Dropped" />
      </>
    );
  }

  return (
    <>
      <Draggable
        {...dragHandlers}
        bounds="body"
        handle=".add-note-header"
      >
        {formContent}
      </Draggable>

      {/* Alert Modals */}
      <AlertModal isOpen={modalDroppedAlert} toggle={() => setModalDroppedAlert(false)} message="Please enter a phone number" />
      <AlertModal isOpen={modalDroppedAlertPhone} toggle={() => setModalDroppedAlertPhone(false)} message="Please select an agent and phone number" />
      <AlertModal isOpen={modalDroppedAlertFaild} toggle={() => setModalDroppedAlertFaild(false)} message={callError || 'Failed to initiate call'} />
      <AlertModal isOpen={modalDroppedAlertTwo} toggle={() => setModalDroppedAlertTwo(false)} message="Dropped" />
    </>
  );
};



function RemWrapper({ children, remBaseline = 16, style }) {
  const translateTransformToRem = (transform, remBaseline = 16) => {
    const convertedValues = transform
      .replace("translate(", "")
      .replace(")", "")
      .split(",")
      .map(px => px.replace("px", ""))
      .map(px => parseInt(px, 10) / remBaseline)
      .map(x => `${x}rem`);

    const [x, y] = convertedValues;
    return `translate(${x}, ${y})`;
  };

  const child = React.Children.only(children);
  const editedStyle = {
    ...child.props.style,
    ...style,
    transform: style?.transform
      ? translateTransformToRem(style.transform, remBaseline)
      : undefined,
  };

  return React.cloneElement(child, {
    ...child.props,
    style: editedStyle,
  });
}

export default MakeCall;